import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { getErrorMessage } from "services/api";

import {
  ModalShell,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useModal,
} from "components/Modal";
import { Button } from "components/Button";
import { Input } from "components/Input";
import { Select } from "components/Select";
import { RegularText } from "components/Text";
import { VerticalSpace } from "components/Spacing";

import {
  createEmployee,
  updateEmployee,
  reassignEmployee,
  getEmployeesGroups,
} from "./api";
import styles from "./styles.module.scss";

const LEFT_LABEL = 115;
const RIGHT_LABEL = 90;

/** Создание / редактирование сотрудника */
export const EmployeeModal = ({ isEdit, employee, group }) => {
  const queryClient = useQueryClient();
  const { closeModal } = useModal();

  const groupsQuery = useQuery(["employees-groups"], getEmployeesGroups);
  const groupOptions = useMemo(
    () =>
      (groupsQuery.data?.items ?? []).map((g) => ({
        value: String(g.item.usersgroupid),
        label: g.item.name,
      })),
    [groupsQuery.data]
  );

  const [form, setForm] = useState({
    name: employee?.name ?? "",
    code: employee?.code ?? "",
    fiscalname: employee?.fiscalname ?? "",
    card: employee?.card ?? "",
    password: employee?.password ?? "",
  });
  const patch = (update) => setForm((prev) => ({ ...prev, ...update }));

  const [groupOption, setGroupOption] = useState(null);

  useEffect(() => {
    if (!groupOptions.length || groupOption) return;
    const targetId = isEdit
      ? employee?.usersgroupid
      : group?.item?.usersgroupid;
    setGroupOption(
      groupOptions.find((o) => String(o.value) === String(targetId ?? "")) ||
        null
    );
  }, [groupOptions, groupOption, isEdit, employee, group]);

  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const isValid =
    form.name &&
    form.code &&
    form.fiscalname &&
    form.card &&
    form.password &&
    groupOption;

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await updateEmployee({ ...form, usersid: employee.usersid });
        if (String(groupOption.value) !== String(employee.usersgroupid ?? "")) {
          await reassignEmployee({
            usersgroupid: groupOption.value,
            usersid: employee.usersid,
          });
        }
      } else {
        await createEmployee({
          ...form,
          usersgroupid: groupOption.value,
        });
      }
      queryClient.invalidateQueries(["employees"]);
      closeModal();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell width={760}>
      <ModalHeader
        title={isEdit ? employee?.name : form.name || "Новый сотрудник"}
        onClose={closeModal}
      />
      <ModalBody>
        <div className={styles.employeeFormGrid}>
          <div className={styles.employeeFormCol}>
            {isEdit && (
              <Input
                label="Код"
                labelWidth={LEFT_LABEL}
                value={employee?.usersid ?? ""}
                disabled
                inputWidth={220}
              />
            )}
            <Input
              label="ФИО"
              labelWidth={LEFT_LABEL}
              value={form.name}
              onChange={(ev) => patch({ name: ev.target.value })}
              inputWidth={220}
            />
            <Input
              label="Код сотрудника"
              labelWidth={LEFT_LABEL}
              type="number"
              value={form.code}
              onChange={(ev) => patch({ code: ev.target.value })}
              inputWidth={220}
            />
            <Input
              label="ФИО для чека"
              labelWidth={LEFT_LABEL}
              value={form.fiscalname}
              onChange={(ev) => patch({ fiscalname: ev.target.value })}
              inputWidth={220}
            />
          </div>
          <div className={styles.employeeFormCol}>
            {isEdit && <div className={styles.employeeFormSpacer} />}
            <Input
              label="№ карты"
              labelWidth={RIGHT_LABEL}
              type="number"
              value={form.card}
              onChange={(ev) => patch({ card: ev.target.value })}
              inputWidth={220}
            />
            <Input
              label="Пароль"
              labelWidth={RIGHT_LABEL}
              type="password"
              value={form.password}
              onChange={(ev) => patch({ password: ev.target.value })}
              inputWidth={220}
            />
            <Select
              label="Должность"
              labelWidth={RIGHT_LABEL}
              options={groupOptions}
              value={groupOption}
              onChange={setGroupOption}
              isLoading={groupsQuery.isLoading}
              width={220}
            />
          </div>
        </div>
        {!!error && (
          <>
            <VerticalSpace size={12} />
            <RegularText size={13} color="danger">
              {error}
            </RegularText>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          icon="galochka"
          variant="secondary"
          onClick={handleSave}
          disabled={!isValid}
          loading={saving}
        >
          Сохранить
        </Button>
      </ModalFooter>
    </ModalShell>
  );
};
